import { useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'

import {
  salesOwnedDefaultValues,
  type SalesOwnedEnterpriseFormValues,
  salesOwnedEnterpriseSchema,
  toSalesOwnedPayload,
} from './schema'

import styles from './CreateSalesOwnedEnterprise.module.scss'
import fieldStyles from '@/components/FormField/styles.module.scss'
import dialogStyles from '@/pages/app/Enterprises/components/styles.module.scss'
import enterpriseStyles from '@/pages/app/Enterprises/styles.module.scss'

import { isApiError } from '@/api'
import { createSalesLedEnterprise } from '@/api/internal'
import Button from '@/components/Button'
import Checkbox from '@/components/Checkbox'
import DialogWrapper from '@/components/Dialog'
import Input from '@/components/Input'
import InternalAdminRoute from '@/components/InternalAdminRoute'
import Select from '@/components/Select'
import { useToast } from '@/components/Toast/ToastContext'
import { DEFAULT_SEAT_TYPE_HINT, DEFAULT_SEAT_TYPE_OPTIONS, seatTypeLabel } from '@/utils/seatType'

type TextFieldName = Exclude<keyof SalesOwnedEnterpriseFormValues, 'createAdminUserIfMissing' | 'defaultSeatType'>

const SalesOwnedEnterpriseForm = () => {
  const [pendingData, setPendingData] = useState<SalesOwnedEnterpriseFormValues | null>(null)
  const [result, setResult] = useState<{
    enterpriseId: string
    name: string
    message: string
  } | null>(null)
  const { updateToast } = useToast()
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SalesOwnedEnterpriseFormValues>({
    resolver: zodResolver(salesOwnedEnterpriseSchema),
    defaultValues: salesOwnedDefaultValues,
  })
  const createAdmin = useWatch({ control, name: 'createAdminUserIfMissing' })
  const createMutation = useMutation({
    mutationFn: (data: SalesOwnedEnterpriseFormValues) => createSalesLedEnterprise(toSalesOwnedPayload(data)),
    onSuccess: (response, data) => {
      setResult({
        enterpriseId: response.data.enterprise_id,
        name: data.name,
        message: response.data.message,
      })
      setPendingData(null)
      reset()
    },
    onError: (error) => {
      updateToast(
        (isApiError(error) ? error.detail : undefined) ?? 'Couldn’t create the organization. Try again.',
        'error'
      )
    },
  })

  const review = handleSubmit((data) => {
    setPendingData(data)
  })
  const closeReview = () => {
    if (createMutation.isPending) return
    setPendingData(null)
  }
  const confirm = () => {
    if (!pendingData || createMutation.isPending) return
    createMutation.mutate(pendingData)
  }
  const field = (name: TextFieldName, label: string, type = 'text', hint?: string) => (
    <div className={fieldStyles.formGroup}>
      <label htmlFor={`sales-owned-${name}`} className={fieldStyles.label}>
        {label}
      </label>
      <Controller
        name={name}
        control={control}
        render={({ field: input }) => (
          <Input
            id={`sales-owned-${name}`}
            className={fieldStyles.input}
            type={type}
            value={String(input.value)}
            onInput={input.onChange}
            isDisabled={createMutation.isPending}
          />
        )}
      />
      {hint && <span className={fieldStyles.hint}>{hint}</span>}
      {errors[name] && (
        <p className={fieldStyles.errorText} role="alert">
          {errors[name]?.message}
        </p>
      )}
    </div>
  )

  return (
    <div className={enterpriseStyles.formSection}>
      <h2 className={enterpriseStyles.sectionTitle}>Create a sales-owned enterprise</h2>
      <p className={dialogStyles.dialogWarningMessage}>
        If the admin pays for a personal plan, it keeps billing. Ask Support to cancel it in Stripe.
      </p>
      <p className={enterpriseStyles.formDescription}>
        Grants Enterprise Bundle access, including Notetaker, billed through Salesforce. No Stripe billing is set up.
        Access continues until explicitly suspended. This form does not create seat assignments.
      </p>
      {result && (
        <div role="status" className={styles.result}>
          <strong>{result.name}</strong>
          <p>{result.message}</p>
          <Button
            title="Manage organization"
            type="link"
            to={`/app/enterprises/manage?enterpriseId=${encodeURIComponent(result.enterpriseId)}&enterpriseName=${encodeURIComponent(result.name)}`}
          />
        </div>
      )}
      <form className={enterpriseStyles.formFields} onSubmit={review} noValidate>
        {field('name', 'Organization name')}
        {field('domain', 'Domain', 'text', 'Reusing a domain can resume an existing organization.')}
        {field('email', 'Admin email', 'email')}
        <Controller
          name="createAdminUserIfMissing"
          control={control}
          render={({ field: input }) => (
            <Checkbox
              checked={input.value}
              onChange={() => input.onChange(!input.value)}
              label="Create the admin account if it does not exist"
              disabled={createMutation.isPending}
            />
          )}
        />
        {createAdmin && (
          <>
            <p className={enterpriseStyles.formDescription}>
              A new account receives a password-reset email. Existing accounts are reused.
            </p>
            {field('adminFirstName', 'Admin first name')}
            {field('adminLastName', 'Admin last name')}
          </>
        )}
        {field('seats', 'Recorded seats', 'number', 'Informational only. This does not enforce a seat cap.')}
        {field(
          'seatCap',
          'Seat cap (optional)',
          'number',
          'Blocks new members and invites beyond this many seats. Leave empty to let the organization grow.'
        )}
        <div className={fieldStyles.formGroup}>
          <label htmlFor="sales-owned-defaultSeatType" className={fieldStyles.label}>
            Default seat type
          </label>
          <Controller
            name="defaultSeatType"
            control={control}
            render={({ field: input }) => (
              <Select
                id="sales-owned-defaultSeatType"
                value={input.value}
                options={DEFAULT_SEAT_TYPE_OPTIONS}
                onChange={input.onChange}
                isDisabled={createMutation.isPending}
              />
            )}
          />
          <span className={fieldStyles.hint}>{DEFAULT_SEAT_TYPE_HINT}</span>
        </div>
        <Button
          title="Review organization"
          type="dark"
          htmlType="submit"
          onClick={() => {}}
          isDisabled={createMutation.isPending}
        />
      </form>
      <DialogWrapper
        isOpen={pendingData !== null}
        title="Confirm sales-owned enterprise"
        subtitle="Billed through Salesforce, with no Stripe billing."
        close={closeReview}
      >
        {pendingData && (
          <div className={dialogStyles.dialogContent}>
            <dl className={styles.review}>
              <dt>Organization</dt>
              <dd>{pendingData.name}</dd>
              <dt>Domain</dt>
              <dd>{pendingData.domain}</dd>
              <dt>Admin email</dt>
              <dd>{pendingData.email}</dd>
              {pendingData.createAdminUserIfMissing && (
                <>
                  <dt>New admin name</dt>
                  <dd>
                    {pendingData.adminFirstName} {pendingData.adminLastName}
                  </dd>
                </>
              )}
              <dt>Access</dt>
              <dd>Enterprise Bundle</dd>
              <dt>Recorded seats</dt>
              <dd>{pendingData.seats}</dd>
              <dt>Seat cap</dt>
              <dd>{pendingData.seatCap || 'None (can grow)'}</dd>
              <dt>Default seat type</dt>
              <dd>{seatTypeLabel[pendingData.defaultSeatType]}</dd>
            </dl>
            <p className={dialogStyles.dialogWarningMessage}>
              No Stripe billing will be set up. Access has no automatic expiration. Confirm the domain carefully: an
              existing sales-owned organization on this domain may be resumed, and these seats and seat cap replace its
              current ones (an empty cap removes it). A resumed organization keeps its own default seat type. If the
              admin pays for a personal plan, it keeps billing. Ask Support to cancel it in Stripe.
            </p>
            <div className={styles.actions}>
              <Button title="Cancel" type="light" onClick={closeReview} isDisabled={createMutation.isPending} />
              <Button title="Create enterprise" type="dark" onClick={confirm} isLoading={createMutation.isPending} />
            </div>
          </div>
        )}
      </DialogWrapper>
    </div>
  )
}

const CreateSalesOwnedEnterprise = () => (
  <InternalAdminRoute>
    <SalesOwnedEnterpriseForm />
  </InternalAdminRoute>
)

export default CreateSalesOwnedEnterprise
